// AI 金鑰登錄台的純表單邏輯(無 React / DOM → 完整可單元測試)。
//
// 每個 consumer 列各有一份表單狀態。安全鐵則:
//  ①送出成功後 value 立即歸零(saveOk → value:'')——金鑰絕不回填、絕不長留前端狀態。
//  ②reveal 只切換「使用者剛打的字」明暗,與後端遮罩 key_hint 無關(後端永不回明文)。

import type { AiKeyEntry, AiKeySource, AiKeyTestResult } from './aiKeysApi';

// ── consumer 分類(供 UI 附加說明;以 id 正規化比對 → 容忍 `_`/空白/大小寫) ──────
export type ConsumerKind = 'admin' | 'developer-portal' | 'other';

export function consumerKind(consumer: string): ConsumerKind {
  const norm = consumer.trim().toLowerCase().replace(/[\s_]+/g, '-');
  if (norm === 'admin') return 'admin';
  if (norm === 'developer-portal' || norm === 'dev-portal' || norm === 'devportal') return 'developer-portal';
  return 'other';
}

// ── 分組(契約 v4:group=admin / dev-portal;登錄台依組顯示) ────────────────────
export type ConsumerGroupKey = 'admin' | 'dev-portal' | 'other';

const GROUP_LABEL_DEFAULT: Record<ConsumerGroupKey, string> = {
  admin: 'Admin 管理助理',
  'dev-portal': '開發者入口 Dev Portal',
  other: '其他消費端',
};

/** 正規化分組鍵(容忍 `_`/空白/大小寫、developer-portal 舊寫法)。 */
export function normalizeGroupKey(raw: string): ConsumerGroupKey {
  const norm = raw.trim().toLowerCase().replace(/[\s_]+/g, '-');
  if (norm === 'admin') return 'admin';
  if (norm === 'dev-portal' || norm === 'developer-portal' || norm === 'devportal') return 'dev-portal';
  return 'other';
}

/**
 * 一筆 entry 的有效分組:優先讀後端權威 `group`,缺省(或 other)時自 `consumer` 推導
 * (`admin` → admin;`dev-portal-*` / `developer-portal-*` → dev-portal)。
 */
export function effectiveGroup(entry: AiKeyEntry): ConsumerGroupKey {
  if (entry.group) {
    const g = normalizeGroupKey(entry.group);
    if (g !== 'other') return g;
  }
  const c = entry.consumer.trim().toLowerCase().replace(/[\s_]+/g, '-');
  if (c === 'admin') return 'admin';
  if (c.startsWith('dev-portal') || c.startsWith('developer-portal') || c.startsWith('devportal')) return 'dev-portal';
  return 'other';
}

export interface AiKeyGroup {
  key: ConsumerGroupKey;
  label: string;
  entries: AiKeyEntry[];
}

/**
 * 把 entries 依有效分組收斂成有序 group(admin → dev-portal → other),組內維持輸入序。
 * group 顯示名優先取該組首個帶 `group_label` 的 entry,否則退回內建預設名。
 */
export function groupAiKeys(entries: AiKeyEntry[]): AiKeyGroup[] {
  const order: ConsumerGroupKey[] = ['admin', 'dev-portal', 'other'];
  const buckets = new Map<ConsumerGroupKey, AiKeyEntry[]>();
  const labels = new Map<ConsumerGroupKey, string>();
  for (const e of entries) {
    const g = effectiveGroup(e);
    if (!buckets.has(g)) buckets.set(g, []);
    buckets.get(g)!.push(e);
    if (!labels.has(g) && e.group_label) labels.set(g, e.group_label);
  }
  const result: AiKeyGroup[] = [];
  for (const g of order) {
    const items = buckets.get(g);
    if (items && items.length) result.push({ key: g, label: labels.get(g) ?? GROUP_LABEL_DEFAULT[g], entries: items });
  }
  return result;
}

/** dev-portal 保留槽:依 consumer 細分消費端描述(assistant / diagnostics)。 */
export function reservedConsumerDescriptor(consumer: string): string {
  const c = consumer.trim().toLowerCase();
  if (c.includes('diagnostic')) return 'AI 診斷';
  if (c.includes('assistant')) return 'AI 助理';
  return 'AI 功能';
}

// ── 狀態顯示 helper ────────────────────────────────────────────────────────────

/** 來源顯示名(UI 設定 / 後端環境變數 / 未設定)。 */
export function sourceLabel(source: AiKeySource): string {
  switch (source) {
    case 'stored':
      return 'UI 設定';
    case 'env':
      return '後端環境變數';
    default:
      return '未設定';
  }
}

/** 遮罩金鑰提示:後端已回遮罩(如 `••••1234`)就照用;已設定但無 hint 時退回通用遮罩。 */
export function keyHintText(entry: AiKeyEntry): string {
  if (entry.key_hint) return entry.key_hint;
  return entry.key_configured ? '••••••••' : '';
}

// ── per-consumer 表單狀態機 ─────────────────────────────────────────────────────

export type KeyFormNotice = { kind: 'none' } | { kind: 'saved' } | { kind: 'error'; message: string; requestId: string | null };

export interface KeyFormState {
  /** 使用者剛打進的金鑰明文(僅存於此列的暫態,送出成功即歸零)。 */
  value: string;
  /** 是否以明文顯示 value(切換 input type)。 */
  reveal: boolean;
  /** PUT 進行中。 */
  saving: boolean;
  notice: KeyFormNotice;
}

export const initialKeyFormState: KeyFormState = { value: '', reveal: false, saving: false, notice: { kind: 'none' } };

export type KeyFormAction =
  | { type: 'edit'; value: string }
  | { type: 'toggleReveal' }
  | { type: 'saveStart' }
  | { type: 'saveOk' }
  | { type: 'saveError'; message: string; requestId: string | null }
  | { type: 'dismiss' };

export function keyFormReducer(state: KeyFormState, action: KeyFormAction): KeyFormState {
  switch (action.type) {
    case 'edit':
      return { ...state, value: action.value, notice: { kind: 'none' } };
    case 'toggleReveal':
      return { ...state, reveal: !state.reveal };
    case 'saveStart':
      return { ...state, saving: true, notice: { kind: 'none' } };
    // 送出成功:欄位歸零、隱藏明文、標示已更新 —— 金鑰絕不回填。
    case 'saveOk':
      return { value: '', reveal: false, saving: false, notice: { kind: 'saved' } };
    case 'saveError':
      return { ...state, saving: false, notice: { kind: 'error', message: action.message, requestId: action.requestId } };
    case 'dismiss':
      return { ...state, notice: { kind: 'none' } };
    default:
      return state;
  }
}

/** 可送出:有非空輸入且不在進行中。 */
export function canSubmitKey(state: KeyFormState): boolean {
  return state.value.trim().length > 0 && !state.saving;
}

/** 清除金鑰的二段確認文案(consumer 專屬;供 ConfirmDialog 使用,亦可單測)。 */
export function clearKeyConfirm(entry: AiKeyEntry): { title: string; warning: string; body: string; confirmLabel: string } {
  return {
    title: `清除「${entry.label}」的金鑰?`,
    warning:
      '清除後將移除以本登錄台設定的金鑰;若後端環境變數另有提供則退回該值,否則此 consumer 將無可用金鑰、相關功能停止運作。',
    body: '此操作無法復原,且會寫入稽核記錄。',
    confirmLabel: '確認清除',
  };
}

// ── 金鑰測試(POST /{consumer}/test)按鈕狀態 + 結果顯示 ─────────────────────────

/** 測試按鈕的暫態(idle → testing → ok / fail)。 */
export type KeyTestState =
  | { status: 'idle' }
  | { status: 'testing' }
  | { status: 'ok'; result: AiKeyTestResult }
  | { status: 'fail'; message: string; requestId: string | null };

export const initialKeyTestState: KeyTestState = { status: 'idle' };

/** 結果 pill 色調(成功=good / 失敗=danger)。 */
export function testResultTone(state: KeyTestState): string {
  if (state.status === 'ok' && state.result.ok) return 'pill-good';
  return 'pill-danger';
}

/** 結果 pill 文案:成功「✓ 連線正常 · model · 延遲」;失敗誠實顯示 error message。 */
export function testResultText(state: KeyTestState): string {
  if (state.status === 'ok') {
    const r = state.result;
    if (r.ok) {
      const lat = r.latency_ms != null ? `${r.latency_ms} ms` : '—';
      return r.model ? `✓ 連線正常 · ${r.model} · ${lat}` : `✓ 連線正常 · ${lat}`;
    }
    return r.error ? `連線失敗:${r.error}` : '連線失敗';
  }
  if (state.status === 'fail') return state.message;
  return '';
}
